<template>
  <VaSidebar
    v-model="writableVisible"
    :width="sidebarWidth"
    :color="color"
    minimized-width="0"
    :key="`sidebar-${adminStore.role}-${isAdmin}`"
  >
    <VaAccordion v-model="value" multiple>
      <VaCollapse v-for="(route, index) in filteredRoutes" :key="`${route.name}-${currentRole}`">
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="route.children ? undefined : { name: getRouteName(route.name) }"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(getDisplayName(route))}`"
            role="button"
            hover-opacity="0.10"
            @click="route.children && toggleManualExpand(index)"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-4 flex items-center gap-2">
              <VaIcon
                v-if="route.meta.icon"
                aria-hidden="true"
                :name="route.meta.icon"
                size="20px"
                :color="iconColor(route)"
              />
              <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold w-full">
                {{ t(getDisplayName(route)) }}
                <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>

        <!-- Dropdown -->
        <template #body>
          <div v-for="(childRoute, index2) in filteredChildren(route.children)" :key="index2">
            <!-- Level 3 -->
            <div v-if="childRoute.children && childRoute.children.length > 0">
              <div>
                <VaSidebarItem
                  :to="undefined"
                  :active="routeHasActiveGrandChild(childRoute)"
                  :active-color="activeColor"
                  :text-color="textColor(childRoute)"
                  :aria-label="`Open category ${t(getDisplayName(childRoute))}`"
                  role="button"
                  hover-opacity="0.10"
                  @click="toggleChildCollapse(index2)"
                >
                  <VaSidebarItemContent class="py-3 pr-2 pl-11 flex items-center gap-2">
                    <VaIcon
                      v-if="childRoute.meta.icon"
                      :name="childRoute.meta.icon"
                      size="20px"
                      :color="iconColor(childRoute)"
                    />
                    <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold w-full">
                      {{ t(getDisplayName(childRoute)) }}
                      <VaIcon :name="getChildArrowDirection(index2)" size="16px" />
                    </VaSidebarItemTitle>
                  </VaSidebarItemContent>
                </VaSidebarItem>

                <div v-show="isChildExpanded(index2)" class="level-3-children">
                  <div v-for="(grandChildRoute, index3) in filteredChildren(childRoute.children)" :key="index3">
                    <VaSidebarItem
                      :to="{ name: getRouteName(grandChildRoute.name) }"
                      :active="isActiveChildRoute(grandChildRoute)"
                      :active-color="activeColor"
                      :text-color="textColor(grandChildRoute)"
                      :aria-label="`Visit ${t(getDisplayName(grandChildRoute))}`"
                      hover-opacity="0.10"
                    >
                      <VaSidebarItemContent class="py-3 pr-2 pl-16 flex items-center gap-2">
                        <VaIcon
                          v-if="grandChildRoute.meta.icon"
                          :name="grandChildRoute.meta.icon"
                          size="18px"
                          :color="iconColor(grandChildRoute)"
                        />
                        <VaSidebarItemTitle class="leading-5 font-semibold text-sm">
                          {{ t(getDisplayName(grandChildRoute)) }}
                        </VaSidebarItemTitle>
                      </VaSidebarItemContent>
                    </VaSidebarItem>
                  </div>
                </div>
              </div>
            </div>

            <!-- Regular child -->
            <div v-else>
              <VaSidebarItem
                :to="{ name: getRouteName(childRoute.name) }"
                :active="isActiveChildRoute(childRoute)"
                :active-color="activeColor"
                :text-color="textColor(childRoute)"
                :aria-label="`Visit ${t(getDisplayName(childRoute))}`"
                hover-opacity="0.10"
              >
                <VaSidebarItemContent class="py-3 pr-2 pl-11 flex items-center gap-2">
                  <VaIcon
                    v-if="childRoute.meta.icon"
                    :name="childRoute.meta.icon"
                    size="20px"
                    :color="iconColor(childRoute)"
                  />
                  <VaSidebarItemTitle class="leading-5 font-semibold">
                    {{ t(getDisplayName(childRoute)) }}
                  </VaSidebarItemTitle>
                </VaSidebarItemContent>
              </VaSidebarItem>
            </div>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>
  </VaSidebar>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, nextTick, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'
import { useAdminStore } from '../../stores/admin'
import { useSidebarPermissions } from './SideBarPermission'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
    tablet: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t } = useI18n()
    const adminStore = useAdminStore()
    
    // Utilisation du composable de permissions
    const {
      currentRole,
      isSupport,
      filterRoutes,
      filterChildren,
      getRouteName,
      getDisplayName
    } = useSidebarPermissions()

    const value = ref<boolean[]>([false])
    const platform = import.meta.env.VITE_APP_PLATFORM || 'local'
    const expandedRoutes = ref<Set<string>>(new Set())
    const expandedChildren = ref<Set<number>>(new Set())
    const manuallyExpandedRoutes = ref<Set<number>>(new Set())

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) return route.name === section.name
      return section.children.some(({ name }) => route.name === name)
    }

    const routeHasActiveGrandChild = (section: INavigationRoute) => {
      if (!section.children) return false
      return section.children.some((child) => {
        if (!child.children) return false
        return child.children.some(({ name }) => route.name === name)
      })
    }

    const toggleChildCollapse = (index: number) => {
      if (expandedChildren.value.has(index)) {
        expandedChildren.value.delete(index)
      } else {
        expandedChildren.value.add(index)
      }
    }

    const isChildExpanded = (index: number) => expandedChildren.value.has(index)

    const getChildArrowDirection = (index: number) => 
      isChildExpanded(index) ? 'va-arrow-up' : 'va-arrow-down'

    const toggleManualExpand = (index: number) => {
      if (manuallyExpandedRoutes.value.has(index)) {
        manuallyExpandedRoutes.value.delete(index)
      } else {
        manuallyExpandedRoutes.value.add(index)
      }
    }

    const setActiveExpand = () => {
      try {
        const routes = filteredRoutes.value
        if (routes.length === 0) return

        const expandedArr = routes.map((route: INavigationRoute, index: number) => {
          const hasActiveChild = routeHasActiveChild(route)
          const hasActiveGrandChild = routeHasActiveGrandChild(route)
          const isManuallyExpanded = manuallyExpandedRoutes.value.has(index)

          if (!isAdmin.value) {
            return hasActiveChild || hasActiveGrandChild || isManuallyExpanded
          }
          return hasActiveChild || hasActiveGrandChild
        })

        value.value = expandedArr.map((shouldExpand, index) => {
          if (manuallyExpandedRoutes.value.has(index)) return true
          return shouldExpand
        })

        filteredRoutes.value.forEach((route) => {
          if (route.children) {
            route.children.forEach((child, index) => {
              if (child.children && routeHasActiveGrandChild(child)) {
                expandedChildren.value.add(index)
              }
            })
          }
        })
      } catch (e) {
        // Silently catch errors
      }
    }

    const sidebarWidth = computed(() => {
      if (props.mobile) return '100vw'
      if (props.tablet) return '70vw'
      return '280px'
    })
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    // Vérifier si l'utilisateur est admin ou super_admin
    // En impersonation (sessionStorage), forcer le rôle de l'agent
    const isAdmin = computed(() => {
      const impersonationSession = sessionStorage.getItem('impersonation_session')
      const impersonationToken = sessionStorage.getItem('impersonation_token')
      // Only consider it impersonating if BOTH session and token exist
      const isImpersonating = !!(impersonationSession && impersonationToken)

      if (isImpersonating) {
        try {
          const agent = JSON.parse(impersonationSession!)
          const agentRole = agent.role || 'agent'
          return agentRole === 'admin' || agentRole === 'super_admin'
        } catch {
          // corrupted data
        }
      }
      const role = localStorage.getItem('role') || adminStore.role || 'agent'
      return role === 'admin' || role === 'super_admin'
    })

    // Filtrer les routes et sections selon le rôle
    const filteredRoutes = computed(() => {
      // Start from full routes list
      let routes = navigationRoutes.routes

      // If NOT CN variant (yd, tk, local), remove top-level users_tab route entirely
      // if (platform !== 'cn' && platform !== 'tk') {
      //   routes = routes.filter((r: INavigationRoute) => r.name !== 'users_tab')
      // }

      if (isAdmin.value) {
        return routes
      }

      // Pour les agents, filtrer certaines routes / sections complètement admin-only
      return routes.filter((route) => {
        const hiddenForAgents = [
          // 'dashboard',
          'announcements',
          'vip-rights',           
          'landing-management',   
          'domain',               
          'versions-section',     
          'videos',               
          'posts',
          'users_tab',                
          'country',  
          'steal_stat',  
          'chat-section',
          'chat-conversations',   
          'content-section',     
          'config',               
          'translate',            
          'game-section',         
          'exchange', 
          'vip-rights',
          'landing-management',
          'domain',
          'versions-section',
          // 'videos',
          'price-config',
          // 'posts',
          'country',
          'config',
          'translate',
          'game-section',
          'exchange',
          'videos',
          'posts',
          'orders',
          'landing',
          'steal_stat',
          'wallet-management',
          'system-settings',
          'admin-agent-withdraw',

         
        ]
        return !hiddenForAgents.includes(route.name)
      })
    })

    // Filtrer les routes enfants selon le rôle
    const filteredChildren = (children?: INavigationRoute[]) => {
      if (!children) return []

      const result = children.filter((child) => {
        // Hide users page entirely when NOT running CN variant (yd) - but we want it visible for yd
        // No condition here - let it be controlled by hiddenForAgents array

        // Routes hidden from agents
        const hiddenForAgents = [
          'announcements',
          //'annonce-editor',
          // 'promotion-link',
          'landing-management',
          'promotion-platform',
          'promotions',
          // Admin-only VIP pages
          'vip-rights', // VIP Rights (hide for agents)
          'agents-vip', // Agents VIP (hide for agents)
          'vip', // VIP Plans (hide for agents)
          // Admin-only pages that should not be visible to agents
          'users',
          'price-config',
          'versions',
          'versions-link',
          'videos',
          'category',
          'chat-log',
          // 'chat-conversations',
          'tag',
          // 'users_tab',     
          'post',
          'category_p',
          'tag_p',
          'emoji',
          'banner',
          'ads',
          'mangas',
          // Also hide these top-level admin sections if they ever appear as children
          'country',
          'config',
          'translate',
          'game-section',
          'exchange',
          'users',
          'price-config',
          'videos',
          'post',
          'users-vip',
          'landing-page',
          'landing',
          'admin-agent-withdraw',
        ]

        // Hide vip-setting for admins only (show for agents)
        if (isAdmin.value && child.name === 'vip-setting') {
          return false
        }

        // Hide promotion-link for admins only (show for agents)
        if (isAdmin.value && child.name === 'promotion-link') {
          return false
        }

        if (!isAdmin.value && hiddenForAgents.includes(child.name)) {
          return false
        }

        // If platform is cn, hide the users page for everyone
        // if (platform === 'cn' && child.name === 'users') return false

        return true
      })

      return result
    }

    watch(
      () => route.fullPath,
      () => {
        nextTick(() => {
          setActiveExpand()
        })
      },
    )

    // Watch pour réagir aux changements de rôle (connexion directe agent)
    watch(
      () => adminStore.role,
      () => {
        nextTick(() => {
          setActiveExpand()
        })
      },
      { immediate: true },
    )

    // Initialize on mount
    onMounted(() => {
      adminStore.getAdminAuthenticate().then(() => {
        nextTick(() => setActiveExpand())
      })
    })

    return {
      writableVisible,
      sidebarWidth,
      value,
      expandedChildren,
      manuallyExpandedRoutes,
      color,
      activeColor,
      navigationRoutes,
      filteredRoutes,
      routeHasActiveChild,
      routeHasActiveGrandChild,
      isActiveChildRoute,
      filteredChildren,
      toggleChildCollapse,
      toggleManualExpand,
      isChildExpanded,
      getChildArrowDirection,
      t,
      iconColor,
      textColor,
      arrowDirection,
      adminStore,
      isAdmin,
      isSupport,
      currentRole,
      getRouteName,
      getDisplayName,
    }
  },
})
</script>

<style scoped>
.level-3-children {
  padding-left: 8px;
  border-left: 1px solid rgba(0, 0, 0, 0.1);
  margin-left: 8px;
}

.dark-theme .level-3-children {
  border-left-color: rgba(255, 255, 255, 0.1);
}
</style>
